import { useState, useMemo, useEffect } from 'react'
import { FormattedMessage } from 'react-intl'
import { Flex, Form, Select, Space, FormItemProps } from 'antd'
import Text from 'shared/components/typography/Text'
import { ChangeHandlerOpts, InputsState } from 'shared/hooks/useSyncNumericInputs'
import CurrencyInput from 'shared/components/CurrencyInput'
import { DEFAULT_FIAT_PRECISION } from 'shared/constants'
import { formKeys } from '../DonationForm/constants'
import { PaymentType } from '../../api/streamer/types'
import { ICurrency } from '../../api/types'
import { FormValues } from '../../types'
import FiatSelect from '../FiatSelect'
import { ConvertAmountArgs } from '../../utils/convert'
import { lang } from './lang'
import s from './s.module.css'
import BaseCurrencySelectLabel from './BaseCurrencySelectLabel'
import QuickAmounts from './QuickAmounts'
import { useError } from './hooks/useError'
import Label from '@/shared/components/Label'
import useCurrencyPrecision from '@/shared/hooks/useCurrencyPrecision.ts'
import { useStreamerConfigContext } from '@/contexts/StreamerConfigContext/StreamerConfigContext'

type Props = {
  paymentType: PaymentType
  baseCurrencies: ICurrency[]
  selectedCryptoAssetCode?: string
  isFinalAmountError: boolean
  minAmountInBaseCurrency: number
  isMinAmountLoading: boolean
  isFinalAmountLoading: boolean
  isPostDonateRequestError: boolean
  inputsState: InputsState
  onSetFiatValue: (value: string, args?: ChangeHandlerOpts<Omit<ConvertAmountArgs, 'value'>>) => void
}

// eslint-disable-next-line max-lines-per-function
export default function Amount({
  paymentType,
  baseCurrencies,
  selectedCryptoAssetCode,
  isFinalAmountError,
  minAmountInBaseCurrency,
  isMinAmountLoading,
  isFinalAmountLoading,
  isPostDonateRequestError,
  inputsState,
  onSetFiatValue,
}: Props) {
  const { username = '' } = useStreamerConfigContext()
  const form = Form.useFormInstance<FormValues>()
  const [isBaseCurrencySelectOpen, setSetBaseCurrencySelectOpen] = useState(false)
  const fiatAmount = Form.useWatch<FormValues[typeof formKeys.fiatAmount]>(formKeys.fiatAmount)
  const baseCurrency = Form.useWatch<FormValues[typeof formKeys.baseCurrency]>(formKeys.baseCurrency)
  const baseCurrencyCode = baseCurrency?.code

  const handleSetBaseCurrency = (value: ICurrency) => {
    form.setFieldValue(formKeys.baseCurrency, value)
    onSetFiatValue(fiatAmount, {
      force: true,
      args: { username, currencyFrom: value.code, currencyTo: selectedCryptoAssetCode },
    })
  }
  const handleQuickAmountClick = (value: string) =>
    onSetFiatValue(value, { args: { username, currencyFrom: baseCurrencyCode, currencyTo: selectedCryptoAssetCode } })

  const amountError = useError({
    isFinalAmountError,
    minAmountInBaseCurrency,
    fiatAmount,
    baseCurrencyCode,
    isPostDonateRequestError,
    isRequestsLoading: !!(isFinalAmountLoading || inputsState.first.isLoading || inputsState.second.isLoading),
  })

  let validateStatus: FormItemProps['validateStatus'] | undefined

  if (amountError) {
    validateStatus = 'error'
  } else {
    validateStatus = 'success'
  }

  useEffect(() => {
    if (amountError) {
      form.scrollToField(formKeys.fiatAmount, { behavior: 'smooth' })
    }
  }, [form, amountError])

  const sortedBaseCurrencies = useMemo(() => {
    const result = baseCurrencies

    const usdIndex = result.findIndex(currency => currency.code === 'USD')
    const usdObject = result[usdIndex]
    if (usdIndex !== -1) {
      result.splice(usdIndex, 1)
    }

    const eurIndex = result.findIndex(currency => currency.code === 'EUR')
    const eurObject = result[eurIndex]
    if (eurIndex !== -1) {
      result.splice(eurIndex, 1)
    }

    if (eurObject) {
      result.unshift(eurObject)
    }

    if (usdObject) {
      result.unshift(usdObject)
    }

    return result
  }, [baseCurrencies])

  const precision = useCurrencyPrecision(selectedCryptoAssetCode || '')

  return (
    <>
      <FiatSelect
        isOpen={isBaseCurrencySelectOpen}
        onClose={() => setSetBaseCurrencySelectOpen(false)}
        options={sortedBaseCurrencies}
        onSelectOption={handleSetBaseCurrency}
        selectedOption={baseCurrency}
      />
      <Flex vertical gap={4}>
        <Label isRequired>
          <FormattedMessage {...lang.amountTitle} />
        </Label>
        <Space.Compact block>
          <Form.Item noStyle name={formKeys.baseCurrency}>
            <Select
              size="large"
              className={s.baseCurrencySelect}
              labelRender={() => <BaseCurrencySelectLabel baseCurrency={baseCurrency} />}
              open={false}
              onClick={() => setSetBaseCurrencySelectOpen(true)}
            />
          </Form.Item>
          <Form.Item name={formKeys.fiatAmount} validateStatus={validateStatus} className={s.fiatAmount}>
            <CurrencyInput
              readOnly={inputsState.first.isLoading}
              isLoading={inputsState.first.isLoading}
              size="large"
              precision={DEFAULT_FIAT_PRECISION}
            />
          </Form.Item>
          {paymentType === 'crypto' && (
            <Form.Item name={formKeys.cryptoAmount} validateStatus={validateStatus} className={s.cryptoAmount}>
              <CurrencyInput
                readOnly={inputsState.second.isLoading}
                isLoading={inputsState.second.isLoading}
                size="large"
                precision={precision}
                prefix={selectedCryptoAssetCode ? <Text color="var(--grey-500)">{selectedCryptoAssetCode}</Text> : null}
              />
            </Form.Item>
          )}
        </Space.Compact>
        {amountError ? (
          <Text fontSize={14} fontType="body" color="var(--danger-light)">
            {amountError}
          </Text>
        ) : null}
        <QuickAmounts
          loading={isMinAmountLoading}
          fiatAmount={fiatAmount}
          onAmountClick={handleQuickAmountClick}
          baseCurrencyCode={baseCurrencyCode}
          minAmountInBaseCurrency={minAmountInBaseCurrency}
        />
      </Flex>
    </>
  )
}
